import _ from 'underscore';
import Promise from 'bluebird';
import Backbone from 'backbone';
import $ from 'jquery';
import ko from 'knockout';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import '@bingads-webui-campaign/ko-stop-binding';
import template from './template.pug';

/**
 * AudienceAdvancedSettings class
 * The class defines the Advanced settings for Audience Campaign/Adgroup
 * It currently supports and is used by only Audience AdGroups.
 *
 * @class AudienceAdvancedSettings
 */
export class AudienceAdvancedSettings extends Backbone.View {
  /**
  * Initializes the {AudienceAdvancedSettings} class.
  *
  * @param {Object} options - options for initializing the class.
  * @returns {undefined}
  */
  initialize(options) {
    this.viewContext = options.viewContext;
    this.model = options.viewContext.currentAdGroup;
    this.campaign = {};

    this.scenario = options.scenario;
    this.level = options.viewContext.level;
    this.supportCashbackSettings = options.supportCashbackSettings;

    const config = DependencyResolver.get('component/config/index');
    this.isNewUI = config.get('IsNewUI');

    this.i18n = DependencyResolver.get('component/i18n/loader!campaignui/feature/audience-campaign');

    if (this.isNewUI) {
      this.isExclusionsExpanded = ko.observable(false);
    }

    if (this.scenario === 'adgroup-settings') {
      this.initExclusions();
      this.initUrlOptions();
      this.initAdRotation();
    }
  }

  /**
   * Renders the view
   *
   * @returns {View} returns the view itself
   */
  render() {
    this.$el.html(template({
      i18n: this.i18n,
      isSettings: this.scenario === 'adgroup-settings',
      isNewUI: this.isNewUI,
    }));

    // delay init (and render) Ad schedule view
    const jsdata = DependencyResolver.get('component/js-data-campaign/index');
    jsdata.Campaign.find(Number(this.viewContext.currentCampaign.Id))
      .then((campaign) => {
        this.campaign = campaign;
        this.initAdScheduleViewAsync();
      });

    if (this.scenario === 'adgroup-settings') {
      this.$('.url-options').append(this.upgradedUrlOptions.render().$el);

      this.$('.ad-rotation').append(this.adRotation.render().$el);

      if (this.isNewUI) {
        this.$('#setExclusions').collapse('show');
      }
    }

    return this;
  }

  get events() {
    return {
      'show.bs.collapse': 'showExclusions',
      'hide.bs.collapse': 'hideExclusions',
    };
  }

  showExclusions(e) {
    const sectionName = $(e.target).data('section');

    if (this.isNewUI && sectionName === 'ExclusionsSection') {
      this.isExclusionsExpanded(true);
      this.$('.camp-adg-shared-exclusions-dropdown .bedded-icon-marker').toggleClass('expanderImage expandedImage');
    }
  }

  hideExclusions(e) {
    const sectionName = $(e.target).data('section');

    if (this.isNewUI && sectionName === 'ExclusionsSection') {
      this.isExclusionsExpanded(false);
      this.$('.camp-adg-shared-exclusions-dropdown .bedded-icon-marker').toggleClass('expanderImage expandedImage');
    }
  }

  /**
   * Validates the inputs
   *
   * @returns {bool} returns true if validation succeeded
   */
  validate() {
    return this.adScheduleView.validate()
      && (this.upgradedUrlOptions ? this.upgradedUrlOptions.validate() : true);
  }

  /**
   * Get the data that is used as the parameter for saving.
   *
   * @returns {Object} - object representing the data for the adgroup to be saved.
   */
  getData() {
    const params = {};

    const dateSettings = this.adScheduleView.getDateSettings();
    const adScheduleUseSearcherTimeZone = _.result(this.adScheduleView, 'getAdScheduleUseSearcherTimeZone');

    _.extend(params, dateSettings);

    if (this.scenario === 'adgroup-settings') {
      const urlOptions = this.upgradedUrlOptions.toObject();
      if (_.isEmpty(urlOptions.TrackingUrlTemplate)) {
        urlOptions.TrackingUrlTemplate = null;
      }
      if (_.isEmpty(urlOptions.FinalUrlSuffix)) {
        urlOptions.FinalUrlSuffix = null;
      }
      if (_.isEmpty(urlOptions.UrlCustomParameters)) {
        urlOptions.UrlCustomParameters = null;
      }

      const adRotation = this.adRotation.getValues();

      _.extend(params, urlOptions, adRotation);
    }

    if (_.isBoolean(adScheduleUseSearcherTimeZone)) {
      params.AdScheduleUseSearcherTimeZone = adScheduleUseSearcherTimeZone;
    }

    return params;
  }

  /**
   * Save Exclusions
   *
   * @returns {undefined}
   */
  saveExtensions() {
    const exclusionRepository = DependencyResolver.get('campaignui/feature/advanced-settings/util/exclusion-repository');
    const siteExclusions = this.siteExclusionView.getValues();

    const campaignId = this.viewContext.currentCampaign.Id;
    const adgroupId = this.viewContext.currentAdGroup.Id;

    const data = {
      AdGroupId: adgroupId,
      CampaignId: campaignId,
      AdvancedSettings: {
        ExcludedSites: siteExclusions,
      },
    };

    return exclusionRepository.saveExtensions(data, 'adgroup')
      .catch((response) => {
        if (response.NegativeSitesResult) {
          this.siteExclusionView.showErrors(response.NegativeSitesResult);

          if (this.isNewUI) {
            this.$('#setExclusions').collapse('show');
          }

          throw 'exclusion error'; //eslint-disable-line
        }
      });
  }

  /**
   * Initialize Ad rotation view
   *
   * @private
   * @returns {undefined}
   */
  initAdRotation() {
    const AdRotationView = DependencyResolver.get('campaignui/feature/advanced-settings/view/ad-rotation');

    this.adRotation = new AdRotationView(_.result(_.result(this.model, 'AdRotation'), 'RotationType'));
  }

  /**
   * Initialize Exclusions view
   *
   * @private
   * @returns {undefined}
   */
  initExclusions() {
    const exclusionRepository = DependencyResolver.get('campaignui/feature/advanced-settings/util/exclusion-repository');
    const BaseExclusionView = DependencyResolver.get('campaignui/feature/advanced-settings/view/base-exclusion');

    const level = 'adgroup';
    const campaignId = this.viewContext.currentCampaign.Id;
    const adgroupId = this.viewContext.currentAdGroup.Id;

    exclusionRepository.load(campaignId, adgroupId, level)
      .then(([campaignExclusions, adgroupExclusions]) => {
        this.siteExclusionView = new BaseExclusionView({
          exclusions: _.result(adgroupExclusions, 'ExcludedSites'),
          exclusionType: 'site',
          level,
          campaignExclusions: _.result(campaignExclusions, 'ExcludedSites'),
        });
        this.$('.site-exclusions').append(this.siteExclusionView.render().$el);
      });
  }

  /**
   * Initialize Url Options view
   *
   * @private
   * @returns {undefined}
   */
  initUrlOptions() {
    const urlOptionsTemplate = DependencyResolver.get('campaignui/feature/advanced-settings/template/url-options');
    const UpgradedUrlOptions = DependencyResolver.get('component/upgraded-url/view/advancedSettingsUrlOptions');

    this.upgradedUrlOptions = new UpgradedUrlOptions({
      editMode: this.scenario === 'adgroup-settings',
      entityType: 'adgroup',
      trackingTemplate: _.result(this.model, 'TrackingUrlTemplate'),
      finalUrlSuffix: _.result(this.model, 'FinalUrlSuffix'),
      customParameters: _.isEmpty(this.model.UrlCustomParameters) ?
        undefined : this.model.UrlCustomParameters,
      urlOptionsTemplate,
      showUurlTestTool: true,
      isForVNext: this.isNewUI,
      adGroupId: this.model.Id,
    });
  }

  /**
   * Initialize Ad Schedule view
   *
   * @private
   * @returns {Promise} - Promise of initializing ad schedule view
   */
  initAdScheduleViewAsync() {
    if (this.scenario === 'adgroup-settings') {
      return this.updateAdScheduleViewWithContextAsync();
    }

    return new Promise((resolve) => {
      resolve(this.renderAdSchedule());
    });
  }

  /**
   * Update Ad Schedule view in adgroup creation
   *
   * @private
   * @returns {Promise} - Promise of initializing ad schedule view
   */
  updateAdScheduleViewWithContextAsync() {
    const campaignId = this.viewContext.currentCampaign.Id;
    const adgroupId = this.viewContext.currentAdGroup.Id;

    const criterionLoader = DependencyResolver.get('campaignui/feature/base-targets/util/criterionLoader');

    const adgroupCriterions = criterionLoader.load(campaignId, adgroupId, ['DayTimeCriterion']);
    const campaignCriterions = criterionLoader.load(campaignId, '', ['DayTimeCriterion']);

    return Promise.join(adgroupCriterions, campaignCriterions)
      .then(([adgroupTargets, campaignTargets]) => {
        const adGroupSchedulingRows = this.parseSchedulingRows(adgroupTargets);
        const campaignSchedulingRows = this.parseSchedulingRows(campaignTargets, true);

        this.renderAdSchedule(adGroupSchedulingRows, campaignSchedulingRows);
      });
  }

  /**
   * Render Ad Schedule view
   * @param {Array} adGroupSchedulingRows adGroupScheduling rows
   * @param {Array} campaignSchedulingRows campaignScheduling rows
   *
   * @private
   * @returns {undefined}
   */
  renderAdSchedule(adGroupSchedulingRows = [], campaignSchedulingRows = []) {
    // TODO: add timezone
    const isSettings = this.scenario === 'adgroup-settings';
    const adScheduleTimeZoneSetting = isSettings && this.model ?
      this.model.AdScheduleTimeZoneSetting :
      this.campaign.AdScheduleTimeZoneSetting;
    const adScheduleUseSearcherTimeZone = isSettings && this.model ?
      this.model.AdScheduleUseSearcherTimeZone :
      this.campaign.AdScheduleUseSearcherTimeZone;
    const model = {
      adScheduleTimeZoneSetting,
      adScheduleUseSearcherTimeZone,
      dayTimeRanges: adGroupSchedulingRows,
      startDate: _.result(this.model, 'StartDate'),
      endDate: _.result(this.model, 'EndDate'),
      adGroupStatus: _.result(this.model, 'Status'),
    };

    const AdScheduleView = DependencyResolver.get('campaignui/feature/ad-schedule/view/index');
    if (this.adScheduleView) {
      this.adScheduleView.remove();
    }
    this.adScheduleView = new AdScheduleView({
      model,
      level: 'adgroup',
      showDateSettings: true,
      campaignDayTimeRanges: campaignSchedulingRows,
      campaignAdScheduleUseSearcherTimeZone: this.campaign.AdScheduleUseSearcherTimeZone,
      supportCashbackSettings: this.supportCashbackSettings,
    });
    this.$('.ad-schedule').append(this.adScheduleView.render().$el);
  }

  /**
   * Parse SchedulingRows
   * @param {object} targets targets
   * @param {string} resetId reset id optional
   *
   * @private

   * @returns {undefined}
   */
  parseSchedulingRows(targets, resetId = false) {
    const schedulingRows = [];

    _.each(targets, (target) => {
      const criterion = target.Criterion;
      const multiplier = target.CriterionBid.Multiplier;
      let cashbackPercent = _.chain(target)
        .result('CriterionCashback')
        .result('CashbackPercent')
        .value();

      if (+cashbackPercent === -1) {
        cashbackPercent = null;
      }

      if (cashbackPercent != null) {
        cashbackPercent = this.i18n.formatDecimalToFixed(cashbackPercent, { digits: 2 });
      }

      schedulingRows.push({
        Id: resetId ? '' : target.Id,
        Day: criterion.Day,
        StartHour: criterion.FromHour,
        StartMinute: criterion.FromMinute,
        EndHour: criterion.ToHour,
        EndMinute: criterion.ToMinute,
        BidAdjustmentType: multiplier < 0 ? -1 : 1,
        BidAdjustmentAmount: Math.abs(multiplier),
        CashbackPercent: cashbackPercent,
      });
    });

    return schedulingRows;
  }
}
